import { useEffect } from 'react';
import { FaCheckCircle, FaExclamationTriangle, FaInfoCircle, FaTimes, FaTimesCircle } from 'react-icons/fa';
import { IconType } from 'react-icons';
import { usePasStore } from '../pasStore';
import type { Toast, ToastVariant } from '../types';

const ICON: Record<ToastVariant, IconType> = {
  success: FaCheckCircle,
  error: FaTimesCircle,
  warning: FaExclamationTriangle,
  info: FaInfoCircle,
};

const ACCENT: Record<ToastVariant, string> = {
  success: 'var(--color-success-500)',
  error: 'var(--color-error-500)',
  warning: 'var(--color-warning-500)',
  info: 'var(--color-info-500)',
};

const ToastCard: React.FC<{ toast: Toast; onDismiss: (id: string) => void }> = ({ toast, onDismiss }) => {
  useEffect(() => {
    const timer = setTimeout(() => onDismiss(toast.id), 4800);
    return () => clearTimeout(timer);
  }, [toast.id, onDismiss]);

  const Icon = ICON[toast.variant];
  const accent = ACCENT[toast.variant];

  return (
    <div
      className="pointer-events-auto flex w-80 items-start gap-3 rounded-xl border p-3.5 shadow-lg"
      style={{ backgroundColor: 'var(--color-background-paper)', borderColor: 'var(--color-border-light)' }}
      role="status"
    >
      <span className="mt-0.5 flex-shrink-0" style={{ color: accent }} aria-hidden="true">
        <Icon className="h-4 w-4" />
      </span>
      <div className="min-w-0 flex-1">
        <p className="text-sm font-semibold" style={{ color: 'var(--color-text-primary)' }}>
          {toast.title}
        </p>
        {toast.message && (
          <p className="mt-0.5 text-xs" style={{ color: 'var(--color-text-secondary)' }}>
            {toast.message}
          </p>
        )}
      </div>
      <button
        type="button"
        onClick={() => onDismiss(toast.id)}
        aria-label="Dismiss notification"
        className="flex-shrink-0 rounded p-1 hover:bg-[var(--color-hover-background)]"
        style={{ color: 'var(--color-text-secondary)' }}
      >
        <FaTimes className="h-3 w-3" />
      </button>
    </div>
  );
};

export const Toaster: React.FC = () => {
  const toasts = usePasStore((s) => s.toasts);
  const dismissToast = usePasStore((s) => s.dismissToast);

  if (toasts.length === 0) return null;

  return (
    <div className="pointer-events-none fixed bottom-4 right-4 z-[60] flex flex-col gap-2">
      {toasts.map((toast) => (
        <ToastCard key={toast.id} toast={toast} onDismiss={dismissToast} />
      ))}
    </div>
  );
};
